import Phaser from 'phaser';
import { GAME_HEIGHT, Card as CardType } from '@hokm/shared';
import { Card } from '../../../objects/Card';
import { PERSIAN_THEME } from '../../../theme/persianTheme';

export interface DiscardControllerCallbacks {
  onDiscardConfirmed: (cardIds: string[]) => void;
  onRenderHand: () => void;
  getHandCards: () => Card[];
  setHandCardsPlayable: (playable: boolean) => void;
}

/**
 * Manages the 2-player discard mode UI and logic.
 * Combined button shows selection count and becomes clickable when ready.
 */
export class DiscardController {
  private scene: Phaser.Scene;
  private callbacks: DiscardControllerCallbacks;

  private active: boolean = false;
  private requiredCount: number = 0;
  private selectedCardIds: string[] = [];
  private discardBtn: Phaser.GameObjects.Container | null = null;
  private discardBtnLabel: Phaser.GameObjects.Text | null = null;
  private discardBtnBg: Phaser.GameObjects.Rectangle | null = null;

  constructor(scene: Phaser.Scene, callbacks: DiscardControllerCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  /**
   * Whether discard mode is currently active.
   */
  get isActive(): boolean {
    return this.active;
  }

  /**
   * Enter discard mode with the given number of cards to discard.
   */
  enter(requiredCount: number): void {
    if (this.active && this.discardBtn) return;

    this.active = true;
    this.requiredCount = requiredCount;
    this.selectedCardIds = [];

    // Update hand to show selectable state
    this.callbacks.onRenderHand();

    // Create combined discard button on the left side, near the cards
    const btnX = 120;
    const btnY = GAME_HEIGHT - 120; // Close to the hand cards
    this.discardBtn = this.scene.add.container(btnX, btnY);

    // Background - starts greyed out
    this.discardBtnBg = this.scene.add
      .rectangle(0, 0, 210, 44, PERSIAN_THEME.colors.tableCenter)
      .setStrokeStyle(2, PERSIAN_THEME.colors.gold);

    // Label - explicitly shows "discard" throughout the process
    const initialText = `Discard: pick ${requiredCount} more`;
    this.discardBtnLabel = this.scene.add
      .text(0, 0, initialText, {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '16px',
        color: PERSIAN_THEME.hex.gold,
      })
      .setOrigin(0.5);

    this.discardBtn.add([this.discardBtnBg, this.discardBtnLabel]);
    this.discardBtn.setSize(210, 44);
    this.discardBtn.setDepth(100);

    // Click handler (only works when ready)
    this.discardBtn.on('pointerdown', () => this.confirm());
  }

  /**
   * Toggle selection of a card for discard.
   */
  toggleSelection(card: Card, cardData: CardType): void {
    if (!this.active) return;

    const cardId = cardData.id;
    const idx = this.selectedCardIds.indexOf(cardId);

    if (idx >= 0) {
      // Deselect
      this.selectedCardIds.splice(idx, 1);
      card.setSelected(false);
    } else if (this.selectedCardIds.length < this.requiredCount) {
      // Select
      this.selectedCardIds.push(cardId);
      card.setSelected(true);
    }

    // Update button state
    this.updateButtonState();
  }

  /**
   * Update the button appearance based on selection state.
   */
  private updateButtonState(): void {
    if (!this.discardBtn || !this.discardBtnLabel || !this.discardBtnBg) return;

    const selected = this.selectedCardIds.length;
    const isReady = selected === this.requiredCount;

    if (isReady) {
      // Ready to discard - make it clickable and highlighted
      this.discardBtnLabel.setText('Discard now!');
      this.discardBtnLabel.setColor(PERSIAN_THEME.hex.textPrimary);
      this.discardBtnBg.setFillStyle(PERSIAN_THEME.colors.jade);
      this.discardBtn.setInteractive({ useHandCursor: true });

      // Add hover effects
      this.discardBtn.off('pointerover').off('pointerout');
      this.discardBtn.on('pointerover', () => {
        this.discardBtnBg?.setFillStyle(PERSIAN_THEME.colors.buttonHover);
      });
      this.discardBtn.on('pointerout', () => {
        this.discardBtnBg?.setFillStyle(PERSIAN_THEME.colors.jade);
      });
    } else {
      // Not ready - show how many more cards needed with "discard" context
      const remaining = this.requiredCount - selected;
      this.discardBtnLabel.setText(`Discard: pick ${remaining} more`);
      this.discardBtnLabel.setColor(PERSIAN_THEME.hex.gold);
      this.discardBtnBg.setFillStyle(PERSIAN_THEME.colors.tableCenter);
      this.discardBtn.disableInteractive();
      this.discardBtn.off('pointerover').off('pointerout');
    }
  }

  /**
   * Confirm the discard selection and send to server.
   */
  private confirm(): void {
    if (this.selectedCardIds.length !== this.requiredCount) return;

    // Disable further interaction
    if (this.discardBtn && this.discardBtnLabel) {
      this.discardBtn.disableInteractive();
      this.discardBtnLabel.setText('Waiting...');
      this.discardBtn.off('pointerover').off('pointerout');
    }
    this.callbacks.setHandCardsPlayable(false);

    // Send to server - UI cleared when cardsDiscarded event received
    this.callbacks.onDiscardConfirmed(this.selectedCardIds);
  }

  /**
   * Exit discard mode and clean up UI.
   */
  exit(): void {
    this.active = false;
    this.selectedCardIds = [];

    if (this.discardBtn) {
      this.discardBtn.destroy();
      this.discardBtn = null;
      this.discardBtnLabel = null;
      this.discardBtnBg = null;
    }
  }

  destroy(): void {
    this.exit();
  }
}
